/**
 * 世界同步 WebSocket 客户端(Phase 3)。连服务端,收全量快照 + 后续增量事件,
 * 驱动同一个 World。断线指数退避重连。所有观众看到同一个世界。
 */
import type { World } from '../engine/World';
import { NET } from '../config';

type Status = (connected: boolean) => void;

export class WorldSocket {
  private ws?: WebSocket;
  private backoff = 1000;
  private closed = false;
  private pingTimer?: number;
  connected = false;
  onStatus?: Status;
  onFull?: (full: boolean) => void;   // 服务端满员 → true;成功连上 → false

  constructor(private world: World, private url: string = NET.wsUrl) {}

  connect() {
    if (this.closed) return;
    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = () => { this.connected = true; this.backoff = 1000; this.onStatus?.(true); this.onFull?.(false); this.startPing(); };
    this.ws.onmessage = (e) => this.onMessage(e.data as string);
    this.ws.onclose = () => { this.connected = false; this.stopPing(); this.onStatus?.(false); this.scheduleReconnect(); };
    this.ws.onerror = () => { try { this.ws?.close(); } catch { /* noop */ } };
  }

  // 每 15s 发一次心跳,让服务端「按活跃连接实时计数 + 超时剔除」成立(掉线 ≤40s 被剔除)
  private startPing() {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      try { this.ws?.send('{"type":"ping"}'); } catch { /* noop */ }
    }, 15000);
  }
  private stopPing() { if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = undefined; } }

  private onMessage(data: string) {
    let msg: any;
    try { msg = JSON.parse(data); } catch { return; }
    if (msg.type === 'full') { this.onFull?.(true); return; }   // 满员:显示排队,靠退避重连自动恢复
    if (msg.type === 'snapshot' || msg.type === 'state') {
      if (msg.state) this.world.applyServerState(msg.state);
    } else if (msg.type === 'heartbeat') {
      if (msg.state) this.world.applyServerState(msg.state);      // 先收敛权威态
      if (msg.events?.length) this.world.dispatch({ events: msg.events, ambient_mood: msg.ambient_mood ?? 'calm' });
    }
  }

  private scheduleReconnect() {
    if (this.closed) return;
    this.backoff = Math.min(this.backoff * 1.6, 15000);
    setTimeout(() => this.connect(), this.backoff);
  }

  /** HTTP 基址(由 wsUrl 推导),供 /claim /ledger 调用 */
  httpBase(): string {
    return this.url.replace(/^ws/, 'http').replace(/\/ws$/, '');
  }

  close() { this.closed = true; this.stopPing(); try { this.ws?.close(); } catch { /* noop */ } }
}
